import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '@/services/apiClient';
import { apiConfig } from '@/config/apiConfig';
import {
  BrandfolderAsset,
  BrandfolderLabelResponse,
  DamFilters,
} from '../types/brandfolder';

interface UseBrandfolderPhotosReturn {
  currentLabelId: string | undefined;
  currentLabel: BrandfolderLabelResponse | null;
  assets: BrandfolderAsset[];
  filteredAssets: BrandfolderAsset[];
  loading: boolean;
  error: string | null;
  hasNextPage: boolean;
  filters: DamFilters;
  selectLabel: (labelId?: string) => void;
  loadMore: () => void;
  refetch: () => void;
  setFilters: (filters: Partial<DamFilters>) => void;
}

export const useBrandfolderPhotos = (): UseBrandfolderPhotosReturn => {
  const [currentLabelId, setCurrentLabelId] = useState<string | undefined>(undefined);
  const [currentLabel, setCurrentLabel] = useState<BrandfolderLabelResponse | null>(null);
  const [allAssets, setAllAssets] = useState<BrandfolderAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [filters, setFiltersState] = useState<DamFilters>({
    page_number: 1,
    page_size: 20
  });

  const baseUrl = apiConfig.endpoints.assetManagement.impact.brandfolder.list;

  // Use ref to avoid stale closures in selectLabel/loadMore/refetch
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const currentLabelIdRef = useRef(currentLabelId);
  currentLabelIdRef.current = currentLabelId;

  /**
   * Safely extract the label response from the API.
   * Handles both `{ data: { assets, child_labels, ... } }` and direct `{ assets, ... }` formats.
   */
  const parseResponse = useCallback((responseData: any): BrandfolderLabelResponse => {
    const inner = responseData?.data ?? responseData;

    const assets: BrandfolderAsset[] = Array.isArray(inner?.assets)
      ? inner.assets.map((a: any) => ({
          bf_asset_id: a.bf_asset_id ?? a.id ?? '',
          url: a.url ?? '',
          status: a.status ?? 'Under Review'
        }))
      : [];

    return {
      label_id: inner?.label_id ?? '',
      label_name: inner?.label_name ?? '',
      child_labels: Array.isArray(inner?.child_labels) ? inner.child_labels : [],
      assets
    };
  }, []);

  const fetchLabel = useCallback(async (
    labelId: string | undefined,
    pageNumber: number,
    pageSize: number,
    append: boolean
  ) => {
    try {
      setLoading(true);
      setError(null);

      const url = labelId ? `${baseUrl}/${labelId}` : baseUrl;
      const response = await apiClient.get(url, {
        params: { page_number: pageNumber, page_size: pageSize }
      });

      const data = parseResponse(response.data);
      setCurrentLabel(data);

      if (append) {
        setAllAssets(prev => [...prev, ...data.assets]);
      } else {
        setAllAssets(data.assets);
      }

      setHasNextPage(data.assets.length >= pageSize);
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load Brandfolder data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [baseUrl, parseResponse]);

  // Initial load — fetch root
  useEffect(() => {
    fetchLabel(undefined, 1, filters.page_size, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Select a label — resets pagination, fetches assets for that label
  const selectLabel = useCallback((labelId?: string) => {
    setCurrentLabelId(labelId);
    setFiltersState(prev => ({ ...prev, page_number: 1 }));
    setAllAssets([]);
    fetchLabel(labelId, 1, filtersRef.current.page_size, false);
  }, [fetchLabel]);

  const loadMore = useCallback(() => {
    if (loading || !hasNextPage) return;

    const nextPage = filtersRef.current.page_number + 1;
    setFiltersState(prev => ({ ...prev, page_number: nextPage }));
    fetchLabel(currentLabelIdRef.current, nextPage, filtersRef.current.page_size, true);
  }, [loading, hasNextPage, fetchLabel]);

  const refetch = useCallback(() => {
    setFiltersState(prev => ({ ...prev, page_number: 1 }));
    fetchLabel(currentLabelIdRef.current, 1, filtersRef.current.page_size, false);
  }, [fetchLabel]);

  const setFilters = useCallback((newFilters: Partial<DamFilters>) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Client-side status filtering
  const filteredAssets = filters.status
    ? allAssets.filter(asset => asset.status === filters.status)
    : allAssets;

  return {
    currentLabelId,
    currentLabel,
    assets: allAssets,
    filteredAssets,
    loading,
    error,
    hasNextPage,
    filters,
    selectLabel,
    loadMore,
    refetch,
    setFilters
  };
};
